package er.prateek.crossly.ui.screens.riddle

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import er.prateek.crossly.data.model.GameMode
import er.prateek.crossly.ads.AdPlacement
import er.prateek.crossly.data.model.DifficultyTier
import er.prateek.crossly.di.AppContainer
import er.prateek.crossly.firebase.AnalyticsEvents
import er.prateek.crossly.ui.components.GameScreen
import er.prateek.crossly.ui.components.GameTopBar
import er.prateek.crossly.ui.components.ModeLevelItem
import er.prateek.crossly.ui.components.ModeLevelMap
import er.prateek.crossly.ui.screens.math.tierLabel
import er.prateek.crossly.ui.theme.Spacing

private val MODE = GameMode.RIDDLES.analyticsName

/** Tier + level map for the Rapid-Fire Riddles mode. */
@Composable
fun RiddleLevelSelectScreen(
    startTier: DifficultyTier,
    onOpenLevel: (levelId: String) -> Unit,
    onBack: () -> Unit,
) {
    val progression = AppContainer.riddleProgressionManager
    val levelsPerTier = AppContainer.appConfig().modes.riddles.levelsPerTier
    var selectedTier by remember { mutableStateOf(startTier) }

    fun solvedCount(tier: DifficultyTier): Int =
        (1..levelsPerTier).count { progression.starsFor(progression.levelId(tier, it)) > 0 }

    val levels: List<ModeLevelItem> = run {
        val firstUnstarred = (1..levelsPerTier).firstOrNull {
            progression.isLevelUnlocked(selectedTier, it - 1) && progression.starsFor(progression.levelId(selectedTier, it)) == 0
        }
        (1..levelsPerTier).map { index ->
            val unlocked = progression.isLevelUnlocked(selectedTier, index - 1)
            ModeLevelItem(
                index = index,
                stars = progression.starsFor(progression.levelId(selectedTier, index)),
                locked = !unlocked,
                isNext = unlocked && index == firstUnstarred,
            )
        }
    }

    GameScreen(adPlacement = AdPlacement.RIDDLE_LEVEL_SELECT) {
        Column(
            modifier = Modifier.fillMaxSize(),
            verticalArrangement = Arrangement.spacedBy(Spacing.lg),
        ) {
            GameTopBar(title = "Math Riddles", subtitle = "Beat the clock", onBack = onBack)
            ModeLevelMap(
                tiers = DifficultyTier.entries,
                selectedTier = selectedTier,
                onSelectTier = { t ->
                    AppContainer.analytics.logEvent(
                        AnalyticsEvents.TIER_SELECTED,
                        mapOf("mode" to MODE, "tier" to t.name, "unlocked" to progression.isTierUnlocked(t), "solved_count" to solvedCount(t), "total_count" to levelsPerTier),
                    )
                    selectedTier = t
                },
                tierUnlocked = { progression.isTierUnlocked(it) },
                tierLabel = { tierLabel(it) },
                tierProgress = { solvedCount(it) to levelsPerTier },
                levels = levels,
                levelTitle = { "Set $it" },
                onOpenLevel = { idx ->
                    AppContainer.analytics.logEvent(
                        AnalyticsEvents.LEVEL_TILE_TAPPED,
                        mapOf("mode" to MODE, "tier" to selectedTier.name, "index" to idx, "stars" to progression.starsFor(progression.levelId(selectedTier, idx))),
                    )
                    onOpenLevel(progression.levelId(selectedTier, idx))
                },
                modifier = Modifier.weight(1f),
            )
        }
    }
}
